import { useEffect } from 'react';

import { useErrorBoundary } from 'react-error-boundary';

import type { AxiosError } from 'axios';

import type { AnalyticEvent, CommonResponse } from '@shared/index';
import {
  BoundaryErrorType,
  FlkCode,
  getMismatches,
  sendRequestErrorsAnalytics,
} from '@shared/index';
import type { Mismatch } from '@shared/types/common-response';

import type { OtpHandleError } from '../model/types';

interface UseHandleOtpCodeErrors {
  error: AxiosError<CommonResponse> | null;
  handleError: OtpHandleError;
  setCustomShowError?: (mismatches?: Mismatch[]) => void;
  analyticEvent?: AnalyticEvent;
}

export const useHandleOtpCodeErrors = ({
  error,
  handleError,
  setCustomShowError,
  analyticEvent,
}: UseHandleOtpCodeErrors) => {
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    if (!error) return;

    let isLimitExhaustedError = false;

    const mismatches = getMismatches(error);
    const isFlkError = mismatches?.reduce((result, { code }) => {
      if (
        code === FlkCode.OTP_0201_0001 ||
        code === FlkCode.OTP_0201_0002 ||
        code === FlkCode.OTP_0201_0003 ||
        code === FlkCode.OTP_0201_0004
      ) {
        if (code === FlkCode.OTP_0201_0004) {
          isLimitExhaustedError = true;
        }

        result = true;
      }
      return result;
    }, false);

    if (isFlkError) {
      handleError({
        isLimitExhaustedError,
      });
    } else {
      if (!!analyticEvent) {
        sendRequestErrorsAnalytics(analyticEvent, mismatches);
      }

      if (!setCustomShowError) {
        showBoundary(BoundaryErrorType.SomethingWentWrong);
        return;
      }

      setCustomShowError(mismatches);
    }
  }, [error, analyticEvent, handleError, setCustomShowError]);
};
